import DefaultLayout from "@/layouts/default";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import FormContact from "src/components/contact-sales/Form";
import Image from "next/image";
import { useMemo, useState } from "react";
import { useRouter } from "next/router";
// import { notification } from "antd";
import MailIcon from '@/public/icons/mail-02.svg'
import VN from '@/public/icons/VN.svg'
import CA from '@/public/icons/CA.svg'
import Phone from '@/public/icons/phone.svg'
import Address from '@/public/icons/marker-pin.svg'
import { Button } from "antd";
import Link from 'next/link'

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function ContactSales() {
  const { t } = useTranslation("contact-sales");
  const router = useRouter();
  // const [api, contextHolder] = notification.useNotification();

  const [selectedSolution, setSelectedSolution] = useState(
    "vulnerability_assessment"
  );
  const solutions = [
    {
      value: "vulnerability_assessment",
      label: t("security_audit.title"),
    },
    {
      value: "vulnerability_management",
      label: t("security_operations.title"),
    },
    {
      value: "data_loss_prevention",
      label: t("data_security.title"),
    },
    {
      value: "trust_center",
      label: t("security_compliance.title"),
    },
    {
      value: "smart_contract_audit",
      label: t("blockchain_security.title"),
    },
  ];

  const onChangeSolution = (value) => {
    setSelectedSolution(value);
  };

  const onSubmitSuccess = () => {
    api.success({
      message: t("success", { ns: "common" }),
      description: t("contact_sale_success", { ns: "common" }),
      placement: "topRight",
    });
  };

  const solutionInformation = useMemo(() => {
    switch (selectedSolution) {
      case "vulnerability_assessment":
        return {
          title: t("security_audit.title"),
          description: t("security_audit.description"),
          image: `/images/contact-sales/vulnerability_assessment-${router.locale}.svg`,
          solutions: [
            {
              title: t("security_audit.penetration_testing"),
              link: '/services/penetration-testing'
            },
            {
              title: t("security_audit.infrastructure_audit"),
              link: '/services/infrastructure'
            },
            {
              title: t("security_audit.red_teaming"),
              link: '/red-teaming'
            },
            {
              title: t("security_audit.performance_testing"),
              link: '/services/performance-testing'
            },
          ],
        };
      case "vulnerability_management":
        return {
          title: t("security_operations.title"),
          description: t("security_operations.description"),
          image: `/images/contact-sales/vulnerability_management-${router.locale}.svg`,
          solutions: [
            {
              title: t("security_operations.cystack_endpoint"),
              link: '/endpoint'
            },
            {
              title: t("security_operations.managed_bug_bounty"),
              link: '/services/managed-bug-bounty'
            },
            {
              title: t("security_operations.vulnerability_management"),
              link: '/services/vulnerability-management'
            },
            {
              title: t("security_operations.security_monitoring"),
              link: '/services/security-monitoring'
            },
            {
              title: t("security_operations.incident_response"),
              link: '/services/incident-response'
            },
          ],
        };
      case "data_loss_prevention":
        return {
          title: t("data_security.title"),
          description: t("data_security.description"),
          image: `/images/contact-sales/data_loss_prevention-${router.locale}.svg`,
          solutions: [
            {
              title: t("data_security.data_leak_detection"),
              link: '/data-leak-detection'
            },
            {
              title: t("data_security.locker_password_manager"),
              link: 'https://locker.io/passwords',
              isExternal: true
            },
            {
              title: t("data_security.locker_secrets_manager"),
              link: 'https://locker.io/secrets',
              isExternal: true
            },
            {
              title: t("data_security.cystack_endpoint"),
              link: '/endpoint'
            },
          ],
        };
      case "trust_center":
        return {
          title: t("security_compliance.title"),
          description: t("security_compliance.description"),
          image: `/images/contact-sales/trust_center-${router.locale}.svg`,
          solutions: [
            {
              title: t("security_compliance.trust_center"),
              link: '/trust-center'
            },
            {
              title: t("security_compliance.security_policy_development"),
              link: '/services/security-policy-development'
            },
            {
              title: t("security_compliance.security_training"),
              link: '/services/security-training'
            },
          ],
        };

      default:
        return {
          title: t("blockchain_security.title"),
          description: t("blockchain_security.description"),
          image: `/images/contact-sales/blockchain_security-${router.locale}.svg`,
          solutions: [
            {
              title: t("blockchain_security.smart_contract_audit"),
              link: '/services/smart-contract-audit'
            },
            {
              title: t("blockchain_security.blockchain_protocol_audit"),
              link: '/services/blockchain-protocol-audit'
            },
            {
              title: t("blockchain_security.safe_chain"),
              link: 'https://safechain.org/',
              isExternal: true
            },
          ],
        };
    }
  }, [selectedSolution, t]);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: "body",
        nonce: undefined,
      }}
    >
      <DefaultLayout
        seoTitle={t("seo.title")}
        seoDescription={t("seo.description")}
      >
        {/* {contextHolder} */}
        <section className="my-20">
          <div className="container-fuild flex items-stretch gap-20 lg:gap-10 contact-sales md:flex-col-reverse">
            <div className="w-[700px] xl:w-[550px] lg:w-[450px] md:hidden md:p-0 p-4 pb-0 pr-0 md:pr-4">
              <div className="bg-cystack-700 background-overview rounded-[20px] flex items-center flex-col justify-center h-full py-16">
                <Image
                  src={solutionInformation.image}
                  width={800}
                  height={600}
                  className="object-contain max-w-full"
                  alt="CyStack contact sales"
                />
                <h4 className="mb-2 font-semibold text-center text-white text-display-sm">
                  {solutionInformation.title}
                </h4>
                <p className="text-md text-[#BABAFF] text-center max-w-[550px] mx-auto">
                  {solutionInformation.description}
                </p>
                <div className="flex max-w-[500px] flex-wrap items-center justify-center gap-2 mt-8">
                  {solutionInformation.solutions.map((item) => (
                    <Link key={item.title} href={item?.link || ''} passHref legacyBehavior>
                      <a target={item.isExternal ? "_blank" : "_self"}>
                        <span
                          key={item.title}
                          className="inline-block px-2 py-1 font-medium bg-white rounded-lg text-cystack-700 text-md"
                        >
                          {item.title}
                        </span>
                      </a>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 border border-solid rounded-xl border-dark-border-section">
                <Image
                  src={"/logo.svg"}
                  width={28}
                  height={28}
                  className="object-contain"
                  alt="CyStack contact sales"
                />
              </div>
              <h2 className="mb-3 text-center text-dark-title text-display-md">
                {t("title")}
              </h2>
              <p className={`${router.locale === 'vi' ? 'max-w-[450px]' : 'max-w-[350px]'} text-center text-dark-new-description text-md mx-auto`}>
                {t("description")}
              </p>
              <div className="mt-8">
                <FormContact
                  solutions={solutions}
                  onChangeSolution={onChangeSolution}
                  onSubmitSuccess={onSubmitSuccess}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="my-20 md:my-12">
          <div className="container-fuild overflow-hidden">
            <div className="bg-[#F4F5F7] rounded-2xl relative py-10">
              <div className="max-w-[400px] pl-8 md:pl-4">
                <h2 className="mb-2 text-display-md md:text-display-sm">{t("need_help")}</h2>
                <p className="text-[#535862] text-lg md:text-md mb-4">{t("need_help_description")}</p>
                <div className="flex items-center gap-2">
                  <MailIcon />
                  <a className="text-lg md:text-md text-primary-500 hover:underline" href="mailto:contact@cystack.net">contact@cystack.net</a>
                </div>
                <a className="text-lg md:text-md text-primary-500 hover:underline" href="mailto:contact@cystack.net">
                  <Button type="primary" className="h-12 mt-8">
                    {t("send_us_email")}
                  </Button>
                </a>
              </div>
              <img
                src="/images/contact/hotline.svg"
                className="absolute top-0 right-0 md:hidden bottom-0 md:left-[50%]"
                alt="CyStack image contact"
              />
            </div>
          </div>
        </section>
        <section className="my-20 md:my-12">
          <div className="container-fuild">
            <h2 className="text-dark-new-title text-display-lg md:text-display-md font-semibold mb-3">{t("location.title")}</h2>
            <p className="text-xl md:text-lg text-[#535862]">{t("location.description")}</p>
            <div className="grid grid-cols-2 md:grid-cols-1 md:gap-8 md:mt-8 gap-16 mt-16">
              <div className="col-span-1">
                <div className="flex items-center gap-3 mb-6">
                  <VN />
                  <span className="text-[32px] md:text-display-sm leading-9 text-dark-new-title font-semibold">{t("location.vn_office")}</span>
                </div>
                <div className="mb-4 text-lg">
                  <div className="flex items-center w-full gap-3 mb-2">
                    <Address />
                    <span className="text-dark-new-title">{t("location.address")}</span>
                  </div>
                  <a className="text-[#3A48F0] underline cursor-pointer font-semibold" href="https://maps.app.goo.gl/w9E9ihvNgcCY6d4dA" target="_blank" rel="noopener noreferrer">
                    Tan Hong Ha Complex, 317 Truong Chinh, Thanh Xuan District, Hanoi, Vietnam
                  </a>
                </div>
                <div className="mb-3 text-lg">
                  <div className="flex items-center w-full gap-3 mb-2">
                    <Phone />
                    <span className="text-dark-new-title">{t("location.phone")}</span>
                  </div>
                  <a href="tel:+84 247 109 9656" className="text-lg text-tertiary-600">(+84) 247 109 9656</a>
                </div>
              </div>
              <div className="col-span-1">
                <div className="flex items-center gap-3 mb-6">
                  <CA />
                  <span className="text-[32px] md:text-display-sm leading-9 text-dark-new-title font-semibold">{t("location.ca_office")}</span>
                </div>
                <div className="mb-4 text-lg">
                  <div className="flex items-center w-full gap-3 mb-2">
                    <Address />
                    <span className="text-dark-new-title">{t("location.address")}</span>
                  </div>
                  <a className="text-[#3A48F0] underline cursor-pointer font-semibold" href="https://maps.app.goo.gl/sJ2FNyTkZy93jYQAA" target="_blank" rel="noopener noreferrer">
                    2376 Dundas St W, Toronto, Ontario M6P 0C1, Canada
                  </a>
                </div>
                <div className="mb-3 text-lg">
                  <div className="flex items-center w-full gap-3 mb-2">
                    <Phone />
                    <span className="text-dark-new-title">{t("location.phone")}</span>
                  </div>
                  <a href="tel:+14373614489" className="text-lg text-tertiary-600">(+1) 437 361 4489</a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </DefaultLayout>
    </GoogleReCaptchaProvider>
  );
}
